/**
 * 「プロジェクトを受け取る」の画面（/transfers/:id・招待のメールのボタンの行き先・2026-10-21 core/handoff-1a）。
 * 判断は projectTransfer.ts（テストあり）。API の正は apps/server/src/modules/platform/projectTransferRoutes.ts。
 *
 * ログインが要る（App.tsx の外側のルートは /login へ送り、ログインの後にここへ戻す = from）。クライアントはアカウントを作るだけで受け取れる
 *   （設計 §2-1・founder の要件。サーバーやコードの置き場の契約は要らない）。
 * 1 画面に置く物（指示書 4）: 誰から・何が移る / 今の持ち主に残る / 外れる・入れ直しを勧める物・お金（受け取った時から利用料はあなたの前払い = 決定 ①）・
 *   受け取れない理由（blockers・入金のリンク）・受け取る / 断る。
 * 受け取る前の確認のダイアログで「取り消せない」を言う（決定 ③ = 待ち時間は置かない）。ダイアログの後に共通の再認証（Reauth.tsx の run）。
 * 受け取った後: 新しい sk_ を 1 回だけ見せる（KeyLine = 鍵の画面と同じ部品）。Functions の鍵の入れ直しに失敗した環境があれば「もう一度公開して」。
 * 別のアカウントで開いた（403 wrong_account）: 伏せ字の宛先と「そのアドレスでログインし直して」（確認のリンクの画面と同じ扱い）。
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type ApiError, type IssuedKey } from '../api';
import { KeyLine, SecretWarning } from '../components/KeyReveal';
import { HandoffLists } from '../components/HandoffLists';
import { useReauth } from '../components/Reauth';
import { ConfirmDialog, ErrorState, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, formatDate, useT } from '../i18n';
import { blockerKeys, canPressAccept, incomingApiPath, statusKey, type AcceptResult, type IncomingView } from '../projectTransfer';
import { TopBar } from './ProjectsPage';

const ACCEPT_ACTION = 'transfer:accept';
const ERRORS = { transfer_closed: 'transfer.error.closed', balance_due: 'transfer.error.funds', transfer_security_hold: 'transfer.error.hold' } as const;

export function TransferIncomingPage() {
  const { id = '' } = useParams();
  const { t } = useT();
  const reauth = useReauth();
  const view = useResource<IncomingView>(incomingApiPath(id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'accept' | 'decline' | null>(null);
  const [declined, setDeclined] = useState(false);
  const [result, setResult] = useState<AcceptResult | null>(null);
  // 受け取った / 断った後は画面の上（見出し）から見せる。WHY: ボタンは長い一覧の下にあり、そのままだと完了の見出しが画面の外に出る（手元の確かめで見つけた）
  useEffect(() => {
    if (result || declined) window.scrollTo(0, 0);
  }, [result, declined]);

  const accept = async () => {
    setConfirm(null);
    setBusy(true);
    setError(null);
    try {
      const r = await reauth.run((proof) => api<AcceptResult>(`${incomingApiPath(id)}/accept`, { method: 'POST', body: { ...proof } }), { action: ACCEPT_ACTION });
      if (r === null) return; // 確認を取り消した = 何も変わっていない
      setResult(r);
    } catch (e) {
      setError(errorText(e as ApiError, ERRORS));
      view.reload();
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setConfirm(null);
    setBusy(true);
    setError(null);
    try {
      await api(`${incomingApiPath(id)}/decline`, { method: 'POST', body: {} });
      setDeclined(true);
    } catch (e) {
      setError(errorText(e as ApiError, ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const v = view.data;
  const projectName = v?.transfer.project?.name ?? '';
  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container narrow" id="main">
        {reauth.dialog}
        {view.loading && !v && <Spinner />}
        {view.error && <LoadError error={view.error} onRetry={view.reload} />}
        {result ? (
          <Done result={result} />
        ) : declined ? (
          <p className="alert alert-ok" role="status">
            {t('transfer.in.declined')}
          </p>
        ) : (
          v && (
            <>
              <PageHeader title={t('transfer.in.title', { project: projectName })} lead={t('transfer.in.from', { email: v.from.email })} />
              {error && (
                <p className="alert alert-error" role="alert">
                  {error}
                </p>
              )}
              {v.transfer.status !== 'invited' ? (
                <p className="alert alert-info" role="status">
                  {t(statusKey(v.transfer.status))}
                </p>
              ) : (
                <>
                  <HandoffLists handoff={v.handoff} details={v.details} side="recipient" />
                  <section className="card">
                    <p>{t('transfer.in.money')}</p>
                    {/* 共同作業者（2026-10-22 core/handoff-1b）: 受け取ると今の手伝いの人は外れる・前の持ち主が「残る」を選んでいれば共同作業者で残る */}
                    {v.transfer.stayAsMember && <p>{t('transfer.in.stays')}</p>}
                    <p className="small muted">{t('transfer.in.membersRemoved')}</p>
                    <p className="small muted">{t('transfer.in.expires', { date: formatDate(v.transfer.expiresAt) })}</p>
                    {blockerKeys(v.blockers).map((k) => (
                      <p key={k} className="alert alert-warn" role="status">
                        {t(k, { date: v.ownerSecurityHoldUntil ? formatDate(v.ownerSecurityHoldUntil) : '' })}
                      </p>
                    ))}
                    {v.blockers.includes('add_funds') && (
                      <p>
                        <Link className="btn btn-ghost" to="/money/prepaid">
                          {t('transfer.in.addFunds')}
                        </Link>
                      </p>
                    )}
                    <div className="row-actions">
                      <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirm('decline')}>
                        {t('transfer.in.decline')}
                      </button>
                      <button type="button" className="btn btn-primary" disabled={!canPressAccept(v, busy)} onClick={() => setConfirm('accept')}>
                        {busy ? t('transfer.in.accepting') : t('transfer.in.accept')}
                      </button>
                    </div>
                  </section>
                </>
              )}
            </>
          )
        )}
        <ConfirmDialog
          open={confirm === 'accept'}
          title={t('transfer.in.confirmTitle', { project: projectName })}
          confirmLabel={t('transfer.in.accept')}
          busy={busy}
          onConfirm={() => void accept()}
          onClose={() => setConfirm(null)}
        >
          <p>{t('transfer.in.confirmBody')}</p>
        </ConfirmDialog>
        <ConfirmDialog
          open={confirm === 'decline'}
          title={t('transfer.in.declineTitle')}
          confirmLabel={t('transfer.in.decline')}
          tone="danger"
          busy={busy}
          onConfirm={() => void decline()}
          onClose={() => setConfirm(null)}
        >
          <p>{t('transfer.in.declineBody')}</p>
        </ConfirmDialog>
      </main>
    </div>
  );
}

/** 読めなかった: 別のアカウント（wrong_account）・無いリンク（404）は分かる言葉で。それ以外は共通のエラー */
function LoadError({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  const { t } = useT();
  if (error.code === 'wrong_account') {
    return (
      <p className="alert alert-warn" role="alert">
        {t('transfer.in.wrongAccount', { email: String(error.details?.email ?? '•••') })}
      </p>
    );
  }
  if (error.status === 404) {
    return (
      <p className="alert alert-warn" role="alert">
        {t('transfer.in.notFound')}
      </p>
    );
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}

/** 受け取った後: 新しい sk_（1 回だけ）・Functions の公開のし直しの案内・プロジェクトへのリンク */
function Done({ result }: { result: AcceptResult }) {
  const { t } = useT();
  const keys: IssuedKey[] = result.newSecretKeys.map((k) => ({ id: k.id, kind: 'secret', environment: k.environment, label: null, token: k.token, createdAt: result.transfer.acceptedAt ?? '' }));
  const where = result.functionsKeys.redeployRequired.map((e) => t(`env.inline.${e as 'development' | 'production'}`)).join(', ');
  return (
    <>
      <PageHeader title={t('transfer.in.done.title', { project: result.project.name })} />
      <section className="card reveal-card">
        <p>{t('transfer.in.done.keys')}</p>
        <SecretWarning />
        {keys.map((k) => (
          <KeyLine key={k.id} k={k} />
        ))}
      </section>
      {where && (
        <p className="alert alert-warn" role="status">
          {t('transfer.in.done.redeploy', { where })}
        </p>
      )}
      <p>
        <Link className="btn btn-primary" to={`/p/${result.project.id}/production/overview`}>
          {t('transfer.in.done.open')}
        </Link>
      </p>
    </>
  );
}
